import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import Decimal from 'decimal.js';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import FullPageLoad from 'components/ui/FullPageLoad';
import BaseWrapper from 'components/layout/BaseWrapper';
import { ButtonFilled } from 'components/ui/Button';
import TournamentLoad from 'components/CreatingTournament/TournamentLoad';
import { RecoveryBanner } from 'components/TournamentJoinPayment/RecoveryBanner';
import { useTournamentJoinPayment } from 'components/TournamentJoinPayment/useTournamentJoinPayment';
import { joinTournament, readyTournament } from 'api/tournamentService';
import * as routes from 'constants/routes';
import { useUser } from 'api/userService';
import { useTournament } from 'context/TournamentContext';
import { registerAnonymousUser } from 'utils/auth/anonymousRegistration';
import { useSocket } from 'utils/hooks/useSocket';
import { usePriceConversion } from 'hooks/usePriceConversion';
import { usePlatformFeeRate } from 'hooks/usePlatformFeeRate';

import {
  Wrapper,
  SubTitleLarge,
  Row,
  ErrorInfo,
  Count,
  LoadAnime,
  JoinTitle,
  JoinStatus,
  CountdownLabel,
  CountdownValue,
  DetailRow,
  DetailLabel,
  DetailValue,
  JoinButtonWrap,
  JoinCard,
  JoinLogo,
  UsdHint,
} from './styled';

const formatDateUTC = (dateStr: string): string => {
  const d = new Date(dateStr);
  const day = String(d.getUTCDate()).padStart(2, '0');
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const year = d.getUTCFullYear();
  const minutes = String(d.getUTCMinutes()).padStart(2, '0');
  const ampm = d.getUTCHours() >= 12 ? 'PM' : 'AM';
  const h12 = d.getUTCHours() % 12 || 12;
  return `${day}/${month}/${year} ${String(h12).padStart(2, '0')}:${minutes} ${ampm} (UTC)`;
};

const formatDuration = (startStr: string, endStr: string): string => {
  const diffMs = new Date(endStr).getTime() - new Date(startStr).getTime();
  if (diffMs <= 0) return '---';
  const totalMinutes = Math.floor(diffMs / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  return parts.join(' ') || '< 1m';
};

const useCountdown = (targetDateStr: string | undefined) => {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  return useMemo(() => {
    if (!targetDateStr) return null;
    const diff = new Date(targetDateStr).getTime() - now;
    if (diff <= 0) return null;
    const totalSeconds = Math.floor(diff / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (totalSeconds < 60) return `${seconds}s`;
    const parts: string[] = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (seconds > 0) parts.push(`${seconds}s`);
    return parts.join(' ');
  }, [targetDateStr, now]);
};

const TournamentsUser = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const hash = searchParams.get('hash');
  const navigate = useNavigate();
  const { isLoggedIn } = useUser();
  const socketCtx = useSocket();
  const socket = socketCtx?.socket;
  const { pricePerPol } = usePriceConversion({ polAmount: 1 });
  const platformFeeRate = usePlatformFeeRate();

  // Use tournament data from TournamentContext (already fetched by TournamentWrapper)
  const {
    tournamentDetails,
    tournamentStatus,
    entryFee,
    isPaidTournament,
    joinedStatus,
  } = useTournament();

  const [error, setError] = useState<string | null>(null);
  const [hasJoined, setHasJoined] = useState(false);
  const [isWaitingForOpponent, setIsWaitingForOpponent] = useState(false);
  const [isOpenedStartLoad, setIsOpenedStartLoad] = useState(false);
  const [seconds, setSeconds] = useState(11);
  const [isJoining, setIsJoining] = useState(false);

  const errorRef = useRef<HTMLDivElement>(null);
  const hasHandledRejoinRef = useRef(false);

  const {
    hasExistingSession,
    recoverySession,
    resumeSession,
    dismissSession,
    state: recoveryState,
  } = useTournamentJoinPayment({ tournamentId: id || '' });

  // Derive attributes from TournamentContext data
  const rawData: any = tournamentDetails?.data;
  const attrs = rawData?.attributes ?? rawData?.data ?? rawData;
  const tournamentId = rawData?.id;

  const isDataLoaded = !!attrs;
  const isPaid = isPaidTournament;

  const statusText = useMemo(() => {
    if (!tournamentStatus) return '';
    switch (tournamentStatus) {
      case 'BEFORE_START':
        return 'This tournament has not started yet';
      case 'IN_PROGRESS':
        return 'This tournament has already started';
      case 'FINISHING':
        return 'This tournament is finishing';
      case 'FINISHED':
        return 'This tournament has finished';
      default:
        return '';
    }
  }, [tournamentStatus]);

  const isInProgress = tournamentStatus === 'IN_PROGRESS';
  const isFinished = tournamentStatus === 'FINISHED' || tournamentStatus === 'FINISHING';
  const countdownTarget = isInProgress ? attrs?.end_at : attrs?.start_at;
  const countdown = useCountdown(countdownTarget);

  // Validate hash on mount
  useEffect(() => {
    if (!hash) {
      setError('Tournament hash is required');
    }
  }, [hash]);

  // TEAM_007: After REST join succeeds, explicitly join tournament rooms on the existing socket.
  useEffect(() => {
    if (!hasJoined || !socket || !id) return;
    try {
      socket.emit('joinTournament', { tournamentId: Number(id) });
    } catch (err) {
      console.error('[TournamentsUser][TEAM_007] Failed to emit joinTournament', err);
    }
  }, [hasJoined, socket, id]);

  // TEAM_007: Listen for tournamentStarted on the shared socket.
  useEffect(() => {
    if (!socket || !id) return;

    const handleTournamentStarted = (data: any) => {
      const payloadTournamentId = data?.tournamentId ?? data?.data?.tournamentId;
      if (payloadTournamentId != null && String(payloadTournamentId) !== String(id)) return;

      setIsOpenedStartLoad(true);
      setSeconds(11);
      setTimeout(() => {
        navigate(routes.GAME.replace(':id', id.toString()));
      }, 10000);
    };

    socket.off('tournamentStarted', handleTournamentStarted);
    socket.on('tournamentStarted', handleTournamentStarted);
    return () => { socket.off('tournamentStarted', handleTournamentStarted); };
  }, [socket, id, navigate]);

  // Handle free tournament join
  const handleJoin = useCallback(async () => {
    if (!id || !hash || isJoining) return;

    setIsJoining(true);
    setError(null);

    try {
      if (!isLoggedIn && attrs?.allow_anonymous_users) {
        const credentials = await registerAnonymousUser();
        localStorage.setItem('username', credentials.username);
        localStorage.setItem('email', credentials.email);
      }

      const joinResponse = await joinTournament(id, hash);
      if (joinResponse.status !== 'JOINED') {
        throw new Error('Failed to join tournament');
      }

      setHasJoined(true);
      setIsJoining(false);
    } catch (err: any) {
      console.error('Error joining tournament:', err);
      setIsJoining(false);

      if (err?.uiState) {
        setError(`Payment required: ${err.uiState}. Please use the main join page to complete payment.`);
        return;
      }
      setError(err?.message || 'Failed to join tournament');
    }
  }, [id, hash, isJoining, isLoggedIn, attrs?.allow_anonymous_users]);

  // Handle Pay & Join: navigate to wallet entry; WalletAddressEntry handles GET-first check (E4.2)
  const handlePayAndJoin = useCallback(() => {
    if (!id) return;
    navigate(routes.SET_WALLET_ADDRESS.replace(':id', id), {
      state: {
        from: 'tournament-join',
        tournamentId: id,
        hash,
      },
    });
  }, [id, hash, navigate]);

  const handleStart = async () => {
    try {
      if (!tournamentId) return;
      setIsWaitingForOpponent(true);
      await readyTournament(String(tournamentId));
    } catch (err) {
      console.error('Error in handleStart:', err);
      setIsWaitingForOpponent(false);
    }
  };

  const handleResumeSession = () => {
    if (!recoverySession) return;
    resumeSession();
    navigate(`/payment-qr/${recoverySession.paymentId}`, {
      state: { from: 'tournament-join', tournamentId: id, hash },
    });
  };

  const handleDismissSession = () => {
    dismissSession();
  };

  // Countdown timer for battle start
  useEffect(() => {
    const secondInterval = setInterval(() => {
      setSeconds(prev => (prev - 1 < 0 ? 59 : prev - 1));
    }, 1000);
    return () => clearInterval(secondInterval);
  }, []);

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.focus();
    }
  }, [error]);

  // Rejoin: redirect returning users based on tournament status
  useEffect(() => {
    if (joinedStatus !== true || !id || !tournamentStatus || hasHandledRejoinRef.current) return;

    const preGameStatuses = ['BEFORE_START', 'waiting_for_opponent', 'waiting_for_start'];

    if (tournamentStatus === 'IN_PROGRESS' || tournamentStatus === 'FINISHED' || tournamentStatus === 'FINISHING') {
      hasHandledRejoinRef.current = true;
      navigate(routes.GAME.replace(':id', id.toString()));
    } else if (preGameStatuses.includes(tournamentStatus ?? '')) {
      hasHandledRejoinRef.current = true;
      setHasJoined(true);
    } else if (tournamentStatus === 'CANCELLED') {
      hasHandledRejoinRef.current = true;
      setError('This tournament has been cancelled.');
    }
  }, [joinedStatus, tournamentStatus, id, navigate]);

  // --- RENDER ---

  // Wait for joinedStatus to resolve before rendering join/payment UI
  if (joinedStatus === null) {
    return <FullPageLoad />;
  }

  // Wait for TournamentContext to load data
  if (!isDataLoaded && !error) {
    return <FullPageLoad />;
  }

  if (error) {
    return (
      <BaseWrapper>
        <Wrapper>
          <TournamentLoad>
            <Row>
              <div ref={errorRef} role="alert" aria-live="assertive" tabIndex={-1}>
                <ErrorInfo>{error}</ErrorInfo>
              </div>
              <Link to={'https://tradingbattles.com/#upcoming-tournaments'}>
                <ButtonFilled>Home</ButtonFilled>
              </Link>
            </Row>
          </TournamentLoad>
        </Wrapper>
      </BaseWrapper>
    );
  }

  // After joining: show waiting/countdown
  if (hasJoined) {
    return (
      <BaseWrapper>
        <Wrapper>
          <TournamentLoad>
            {!isOpenedStartLoad && (
              <Row>
                {isWaitingForOpponent ? (
                  <SubTitleLarge>Waiting for opponent...</SubTitleLarge>
                ) : (
                  <>
                    <SubTitleLarge>
                      You are about to join {attrs?.name} {localStorage.getItem('username')} battle!
                    </SubTitleLarge>
                    <ButtonFilled onClick={handleStart}>Start</ButtonFilled>
                  </>
                )}
              </Row>
            )}

            {isOpenedStartLoad && (
              <>
                <SubTitleLarge>The battle starts in</SubTitleLarge>
                {(seconds < 11 || seconds === 0) ? (
                  <Count>{seconds}</Count>
                ) : (
                  <LoadAnime />
                )}
              </>
            )}
          </TournamentLoad>
        </Wrapper>
      </BaseWrapper>
    );
  }

  // Tournament preview (main view)
  // Backend returns stake as a top-level number field, not inside entryFee
  // TB-RULE-DECIMAL-BACKEND: use Decimal.js for all monetary calculations (no Number/parseFloat)
  const stakeDecimal = attrs?.stake != null
    ? new Decimal(String(attrs.stake))
    : (entryFee?.type === 'PAID' && entryFee.amount != null ? new Decimal(String(entryFee.amount)) : new Decimal(0));
  const stakeCurrency = attrs?.reward_pool_currency || entryFee?.currency || 'POL';
  const hasPaidStake = stakeDecimal.greaterThan(0);
  const rewardPoolCurrency = attrs?.reward_pool_currency || 'POL';
  const feeMultiplier = new Decimal(1).minus(new Decimal(platformFeeRate).div(100));
  const rewardPoolDecimal = new Decimal(String(attrs?.reward_pool ?? 0));
  const currentRewardPool = rewardPoolDecimal.mul(feeMultiplier);
  const projectedRewardPool = rewardPoolDecimal.plus(stakeDecimal).mul(feeMultiplier);

  return (
    <BaseWrapper compact>
      <JoinCard>
        <JoinLogo />

        {hasExistingSession && recoverySession && (
          <RecoveryBanner
            session={recoverySession}
            onResume={handleResumeSession}
            onDismiss={handleDismissSession}
            isLoading={recoveryState === 'loading'}
          />
        )}

        <JoinTitle>Tournament {tournamentId}</JoinTitle>
        <JoinStatus>{statusText}</JoinStatus>

        {countdown && !isFinished && (
          <>
            <CountdownLabel>{isInProgress ? 'ends in' : 'starts in'}</CountdownLabel>
            <CountdownValue>{countdown}</CountdownValue>
          </>
        )}

        <DetailRow>
          <DetailLabel>Type:</DetailLabel>
          <DetailValue>{attrs?.trade_type === 'Margin' ? 'Margin trading' : 'Spot trading'}</DetailValue>
        </DetailRow>

        {attrs?.start_at && (
          <DetailRow>
            <DetailLabel>Start:</DetailLabel>
            <DetailValue>{formatDateUTC(attrs.start_at)}</DetailValue>
          </DetailRow>
        )}

        {attrs?.end_at && (
          <DetailRow>
            <DetailLabel>Finish:</DetailLabel>
            <DetailValue>{formatDateUTC(attrs.end_at)}</DetailValue>
          </DetailRow>
        )}

        {attrs?.start_at && attrs?.end_at && (
          <DetailRow>
            <DetailLabel>Duration:</DetailLabel>
            <DetailValue>{formatDuration(attrs.start_at, attrs.end_at)}</DetailValue>
          </DetailRow>
        )}

        <DetailRow>
          <DetailLabel>Market(s):</DetailLabel>
          <DetailValue>Cryptocurrencies</DetailValue>
        </DetailRow>

        {hasPaidStake && (
          <DetailRow>
            <DetailLabel>Stake:</DetailLabel>
            <DetailValue>
              {stakeDecimal.toFixed()} {stakeCurrency}
              <UsdHint>
                {pricePerPol != null
                  ? `≈${stakeDecimal.mul(new Decimal(String(pricePerPol))).toFixed(2)} USD`
                  : '≈-- USD'}
              </UsdHint>
            </DetailValue>
          </DetailRow>
        )}

        {hasPaidStake && (
          <DetailRow>
            <DetailLabel>Reward pool:</DetailLabel>
            <DetailValue>
              {currentRewardPool.toFixed(4)} {rewardPoolCurrency}
              <UsdHint>
                {pricePerPol != null
                  ? `≈${currentRewardPool.mul(new Decimal(String(pricePerPol))).toFixed(4)} USD`
                  : '≈-- USD'}
              </UsdHint>
            </DetailValue>
          </DetailRow>
        )}

        {hasPaidStake && projectedRewardPool.greaterThan(0) && (
          <DetailRow>
            <DetailLabel>After you join:</DetailLabel>
            <DetailValue>
              {projectedRewardPool.toFixed(4)} {rewardPoolCurrency}
              <UsdHint>
                {pricePerPol != null
                  ? `≈${projectedRewardPool.mul(new Decimal(String(pricePerPol))).toFixed(4)} USD`
                  : '≈-- USD'}
              </UsdHint>
            </DetailValue>
          </DetailRow>
        )}

        <JoinButtonWrap>
          {hasPaidStake ? (
            <ButtonFilled
              onClick={handlePayAndJoin}
              disabled={isFinished}
            >
              Pay & Join
            </ButtonFilled>
          ) : (
            <ButtonFilled
              onClick={handleJoin}
              disabled={isJoining || isFinished}
            >
              {isJoining ? 'Joining...' : 'Join'}
            </ButtonFilled>
          )}
        </JoinButtonWrap>
      </JoinCard>
    </BaseWrapper>
  );
};

export default TournamentsUser;
